(function () {
  // Conditional logging setup
  const isDev =
    window.location.hostname.includes('myshopify.com') || window.location.hostname === 'localhost';
  // eslint-disable-next-line no-console
  const log = isDev ? console.log.bind(console) : () => {};
  const warn = isDev ? console.warn.bind(console) : () => {};
  const error = isDev ? console.error.bind(console) : () => {};

  const KLAVIYO_COMPANY_ID = '{{ klaviyo_company_id }}';
  const form = document.getElementById('bis-form-{{ current_variant.id }}');
  if (!form) return; // Guard: form absent when snippet is included on pages without the element
  const container = form.closest('.back-in-stock-form');
  const emailInput = form.querySelector('input[name="email"]');
  const consentCheckbox = form.querySelector('input[name="consent"]');
  const submitButton = form.querySelector('.back-in-stock-form__submit');
  const successDiv = container.querySelector('.back-in-stock-form__success');
  const productId = container.getAttribute('data-product-id');
  const variantId = container.getAttribute('data-variant-id');
  const productTitle = container.getAttribute('data-product-title');
  const variantTitle = container.getAttribute('data-variant-title');

  // Store event handlers for cleanup
  const eventHandlers = {
    formSubmit: null,
    emailBlur: null,
    emailInput: null,
    resetClick: null,
  };

  // Store reference to resetButton for cleanup
  let resetButton = null;

  // Email validation
  function validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  // Show error
  function showError(input, message) {
    const errorSpan = input.nextElementSibling;
    if (errorSpan && errorSpan.classList.contains('back-in-stock-form__error')) {
      errorSpan.textContent = message;
      errorSpan.classList.add('show');
      input.classList.add('error');
    }
  }

  // Clear error
  function clearError(input) {
    const errorSpan = input.nextElementSibling;
    if (errorSpan && errorSpan.classList.contains('back-in-stock-form__error')) {
      errorSpan.textContent = '';
      errorSpan.classList.remove('show');
      input.classList.remove('error');
    }
  }

  // Check for duplicate signup
  function hasPreviousSignup() {
    const key = `bis_signup_${variantId}`;
    const stored = localStorage.getItem(key);
    if (stored) {
      const data = JSON.parse(stored);
      // Check if signup is less than 24 hours old
      const hoursSince = (Date.now() - data.timestamp) / (1000 * 60 * 60);
      return hoursSince < 24 ? data.email : null;
    }
    return null;
  }

  // Store signup
  function storeSignup(email) {
    const key = `bis_signup_${variantId}`;
    try {
      localStorage.setItem(
        key,
        JSON.stringify({
          email: email,
          timestamp: Date.now(),
          productTitle: productTitle,
          variantTitle: variantTitle,
        })
      );
    } catch (_e) {
      /* Safari private mode — localStorage not available, skip */
    }
  }

  // Submit to Klaviyo
  async function submitToKlaviyo(email) {
    if (KLAVIYO_COMPANY_ID === 'YOUR_KLAVIYO_COMPANY_ID' || !KLAVIYO_COMPANY_ID) {
      throw new Error('Klaviyo Company ID not configured');
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
              },
            },
          },
          channels: ['EMAIL'],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    };

    const response = await fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${KLAVIYO_COMPANY_ID}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          revision: '2024-07-15',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      let errorData = {};
      try {
        errorData = await response.json();
      } catch (e) {
        warn('JSON parse error', e);
      }
      throw new Error(errorData.detail || 'Failed to subscribe');
    }

    // Klaviyo returns 202/204 with no body on success; avoid JSON parse crash
    return response.status === 204 ? {} : await response.json();
  }

  // Handle form submission
  eventHandlers.formSubmit = async function (e) {
    e.preventDefault();

    const email = emailInput.value.trim();

    // Clear previous errors
    clearError(emailInput);

    // Validation
    if (!email) {
      showError(emailInput, 'Please enter your email address');
      emailInput.focus();
      return;
    }

    if (!validateEmail(email)) {
      showError(emailInput, 'Please enter a valid email address');
      emailInput.focus();
      return;
    }

    if (!consentCheckbox.checked) {
      showError(consentCheckbox, 'Please agree to receive notifications to continue.');
      consentCheckbox.focus();
      return;
    }

    // Check for duplicate
    const previousEmail = hasPreviousSignup();
    if (previousEmail === email) {
      showError(emailInput, 'You already signed up for notifications with this email.');
      return;
    }

    // Submit
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.classList.add('loading');
      const submitTextEl = submitButton.querySelector('.back-in-stock-form__submit-text');
      if (submitTextEl) submitTextEl.textContent = submitButton.getAttribute('data-loading-text');
    }

    try {
      // Try Klaviyo first
      await submitToKlaviyo(email);

      // Store locally
      storeSignup(email);

      // Show success
      form.style.display = 'none';
      if (successDiv) {
        successDiv.style.display = 'block';
        const emailDisplay = successDiv.querySelector('.back-in-stock-form__email');
        if (emailDisplay) {
          emailDisplay.textContent = email;
        }
      }

      // Track success
      if (typeof gtag !== 'undefined') {
        gtag('event', 'back_in_stock_signup', {
          event_category: 'Engagement',
          event_label: productTitle,
          product_id: productId,
          variant_id: variantId,
        });
      }
    } catch (err) {
      error('Back in stock signup error:', err);

      // Store locally as fallback
      storeSignup(email);

      // Show success anyway (better UX)
      form.style.display = 'none';
      if (successDiv) {
        successDiv.style.display = 'block';
        const emailDisplay = successDiv.querySelector('.back-in-stock-form__email');
        if (emailDisplay) {
          emailDisplay.textContent = email;
        }
      }
    } finally {
      // Reset button state only if it exists
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('loading');
        const submitText = submitButton.querySelector('.back-in-stock-form__submit-text');
        if (submitText) {
          submitText.textContent = submitButton.getAttribute('data-default-text');
        }
      }
    }
  };

  // Add form submit listener with null check
  if (form && eventHandlers.formSubmit) {
    form.addEventListener('submit', eventHandlers.formSubmit);
  }

  // Real-time email validation
  eventHandlers.emailBlur = function () {
    const email = emailInput.value.trim();
    if (email && !validateEmail(email)) {
      showError(emailInput, 'Please enter a valid email address');
    }
  };
  if (emailInput && eventHandlers.emailBlur) {
    emailInput.addEventListener('blur', eventHandlers.emailBlur);
  }

  eventHandlers.emailInput = function () {
    if (emailInput.classList.contains('error')) {
      clearError(emailInput);
    }
  };
  if (emailInput && eventHandlers.emailInput) {
    emailInput.addEventListener('input', eventHandlers.emailInput);
  }

  // Reset form
  resetButton = container.querySelector('.back-in-stock-form__reset');
  if (resetButton) {
    eventHandlers.resetClick = function () {
      form.style.display = 'flex';
      if (successDiv) {
        successDiv.style.display = 'none';
      }
      form.reset();
    };
    resetButton.addEventListener('click', eventHandlers.resetClick);
  }

  // Track form view
  if (typeof gtag !== 'undefined') {
    gtag('event', 'view_back_in_stock_form', {
      event_category: 'Engagement',
      event_label: productTitle,
      product_id: productId,
      variant_id: variantId,
    });
  }

  // Cleanup function
  function cleanup() {
    log('Back in stock form cleanup triggered');
    if (eventHandlers.formSubmit && form) {
      form.removeEventListener('submit', eventHandlers.formSubmit);
    }
    if (eventHandlers.emailBlur && emailInput) {
      emailInput.removeEventListener('blur', eventHandlers.emailBlur);
    }
    if (eventHandlers.emailInput && emailInput) {
      emailInput.removeEventListener('input', eventHandlers.emailInput);
    }
    if (resetButton && eventHandlers.resetClick) {
      resetButton.removeEventListener('click', eventHandlers.resetClick);
    }
    // Clear all handler references
    Object.keys(eventHandlers).forEach((key) => {
      eventHandlers[key] = null;
    });
    resetButton = null;
  }

  // MutationObserver to detect when container is removed from DOM
  function observeContainerRemoval() {
    const observer = new MutationObserver(() => {
      // Check if container is still in the document
      if (!document.contains(container)) {
        cleanup();
        observer.disconnect();
        log('Back in stock form removed from DOM, cleanup completed');
      }
    });

    observer.observe(container.parentNode || document.body, {
      childList: true,
      subtree: true,
    });
  }

  // Start observing for container removal
  if (container.parentNode) {
    observeContainerRemoval();
  }

  // Expose cleanup for the variant form component
  if (!window.BackInStockCleanup) {
    window.BackInStockCleanup = [];
  }
  window.BackInStockCleanup.push(cleanup);
})();
